import React, { useState, useEffect } from 'react';
import { X, Search, Plus, UserPlus } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ContactForm from './ContactForm';

const EVENT_TYPES = [
  { value: 'conference', label: 'Conference' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'networking', label: 'Networking' },
  { value: 'other', label: 'Other' }
];

function EventForm({ event, onClose, onSave }) {
  const { contacts, fetchContacts, createContact } = useApp();
  const [formData, setFormData] = useState({
    title: event?.title || '',
    event_type: event?.event_type || 'other',
    date: event?.date || new Date().toISOString().split('T')[0],
    location: event?.location || '',
    description: event?.description || '',
    attendee_ids: event?.attendee_ids || []
  });
  const [saving, setSaving] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addAttendee = (contact) => {
    if (!formData.attendee_ids.includes(contact.id)) {
      setFormData(prev => ({
        ...prev,
        attendee_ids: [...prev.attendee_ids, contact.id]
      }));
    }
    setAttendeeSearch('');
  };

  const removeAttendee = (contactId) => {
    setFormData(prev => ({
      ...prev,
      attendee_ids: prev.attendee_ids.filter(id => id !== contactId)
    }));
  };

  const handleNewContact = async (contactData) => {
    const newContact = await createContact(contactData);
    await fetchContacts();
    if (newContact?.id) {
      setFormData(prev => ({
        ...prev,
        attendee_ids: [...prev.attendee_ids, newContact.id]
      }));
    }
    setShowContactForm(false);
  };

  const filteredContacts = contacts.filter(c => {
    if (formData.attendee_ids.includes(c.id)) return false;
    if (!attendeeSearch) return true;
    const searchLower = attendeeSearch.toLowerCase();
    const fullName = `${c.first_name} ${c.last_name || ''}`.toLowerCase();
    return fullName.includes(searchLower) ||
           (c.company && c.company.toLowerCase().includes(searchLower));
  });

  const selectedContacts = contacts.filter(c => formData.attendee_ids.includes(c.id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2 className="modal-title">{event ? 'Edit Event' : 'Create Event'}</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-input"
                placeholder="Event title..."
                required
              />
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Event Type *</label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  className="form-input form-select"
                  required
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="form-input"
                placeholder="Conference center, restaurant, Zoom..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-input form-textarea"
                placeholder="Event details, agenda, notes..."
                rows={3}
              />
            </div>

            {/* Attendees Section */}
            <div className="form-group">
              <label className="form-label">
                Attendees ({formData.attendee_ids.length})
              </label>

              {/* Selected attendees as chips */}
              {selectedContacts.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  marginBottom: '0.75rem'
                }}>
                  {selectedContacts.map(contact => (
                    <div
                      key={contact.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        background: 'var(--gray-100)',
                        borderRadius: '2rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <div className="avatar" style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '0.625rem'
                      }}>
                        {contact.first_name[0]}{contact.last_name?.[0] || ''}
                      </div>
                      <span>{contact.first_name} {contact.last_name || ''}</span>
                      <button
                        type="button"
                        onClick={() => removeAttendee(contact.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0',
                          color: 'var(--gray-400)',
                          display: 'flex'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add attendee buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowPicker(!showPicker)}
                >
                  <Plus size={16} />
                  Add Attendee
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowContactForm(true)}
                >
                  <UserPlus size={16} />
                  New Contact
                </button>
              </div>

              {/* Attendee picker */}
              {showPicker && (
                <div style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: '0.5rem',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '0.5rem' }}>
                    <div className="search-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        value={attendeeSearch}
                        onChange={(e) => setAttendeeSearch(e.target.value)}
                        className="form-input search-input"
                        placeholder="Search contacts..."
                        autoFocus
                      />
                    </div>
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {filteredContacts.length === 0 ? (
                      <div style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: 'var(--gray-500)',
                        fontSize: '0.875rem'
                      }}>
                        No contacts found
                      </div>
                    ) : (
                      filteredContacts.slice(0, 10).map(contact => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => addAttendee(contact)}
                          style={{
                            width: '100%',
                            padding: '0.625rem 0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: '1px solid var(--gray-100)',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <div className="avatar" style={{
                            width: '32px',
                            height: '32px',
                            fontSize: '0.75rem'
                          }}>
                            {contact.first_name[0]}{contact.last_name?.[0] || ''}
                          </div>
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                              {contact.first_name} {contact.last_name}
                            </div>
                            {contact.company && (
                              <div style={{
                                fontSize: '0.75rem',
                                color: 'var(--gray-500)'
                              }}>
                                {contact.company}
                              </div>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>

      {showContactForm && (
        <ContactForm
          onClose={() => setShowContactForm(false)}
          onSave={handleNewContact}
        />
      )}
    </div>
  );
}

export default EventForm;
